import cx from 'clsx';
import React, { useState } from 'react';

import { CloseIcon, FlatCloseIcon } from '@/components/icons';
import { MEDIUM_BREAKPOINT } from '@/components/modular-grid/constants';
import Overlay from '@/components/Overlay';
import Portal from '@/components/Portal';
import { useWindowSize } from '@/hooks/useWindowSize';

import styles from './GameDetailsModal.module.scss';

interface GameDetailsModalPropTypes {
  componentVisible?: boolean;
  onModalOverlayClick?: () => void;
  children?: React.ReactNode;
}

const GameDetailsModal: React.FC<GameDetailsModalPropTypes> = props => {
  const { componentVisible = false, children, onModalOverlayClick } = props;

  const { width } = useWindowSize();

  const [closeAnimation, setCloseAnimation] = useState(false);

  if (!children || !componentVisible) return null;

  const size = width <= MEDIUM_BREAKPOINT.max ? 'small' : 'wide';

  /**
   * In the case of animation for the mobile view,
   * we need to wait for the closing animation to be performed before we change the route
   */
  const closeActionInterval = size === 'small' ? 300 : 0;

  function handleCloseClick() {
    setCloseAnimation(true);

    setTimeout(() => {
      onModalOverlayClick?.();

      setCloseAnimation(false);
    }, closeActionInterval);
  }

  const SmallModalCmp = (
    <>
      <div
        data-testid="modal-small"
        className={cx(
          styles.modalMobile,
          !closeAnimation && styles.slideUp,
          closeAnimation && styles.slideDown
        )}
      >
        <div className={styles.modalContent}>{children}</div>
        <div className={cx(styles.modalHeader)}>
          <div
            data-testid="game-details-close-icon"
            className={styles.mobileCloseIcon}
            onClick={handleCloseClick}
          >
            <FlatCloseIcon />
          </div>
        </div>
      </div>
      <Overlay componentVisible={componentVisible} onClick={handleCloseClick} />
    </>
  );

  const LargeModalCmp = (
    <>
      <div
        data-testid="modal-large"
        className={cx(
          styles.modalDesktop,
          !closeAnimation && styles.show,
          closeAnimation && styles.hide
        )}
      >
        <div className={styles.modalContent}>{children}</div>
        <div className={cx(styles.modalHeader)}>
          <div
            data-testid="game-details-close-icon"
            className={styles.closeIcon}
            onClick={handleCloseClick}
          >
            <CloseIcon strokeWidth={3.5} />
          </div>
        </div>
      </div>
      <Overlay componentVisible={componentVisible} onClick={handleCloseClick} />
    </>
  );

  return (
    <Portal
      id={'page-content'}
      children={size === 'small' ? SmallModalCmp : LargeModalCmp}
    />
  );
};

export default GameDetailsModal;
